import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Header from "../Header";
import TabNavbar from "../Tabbar";
import HomeButton from "../HomeButton";
import SpinnerLoader from "../SpinnerLoader";
import Pagination from "../Pagination";
import { RootState, useAppDispatch } from "../../redux/store";
import {
  getAuditTrail,
  downloadAuditTrail,
  AuditFilters,
} from "../../redux/slices/audit.slice";
import { Tabs } from "../../utils/TabbarData";

interface AuditTrailPageProps {
  role: string;
  tabs: Tabs[];
  selectedTabId: number;
  tabType: string;
}

const STATUS_MAP: Record<string, string> = {
  APPLIED: "Pending Manager Confirmation",
  APPROVED_BY_MANAGER: "Manager Approved",
  APPROVED_BY_MANAGER_NO_ALLOW: "Manager Approved (No Travel)",
  REJECTED_BY_MANAGER: "Manager Declined",
  APPLICATION_REJECTED_BY_LEARNER: "User Declined",
  WAITING: "Waiting List",
  DRAFTED: "Drafted",
  CONFIRMED_BY_LEARNER: "Confirmed by Learner",
  CONFIRMED_BY_HR: "Confirmed by HR",
  CONFIRMED_BY_MANAGER: "Confirmed by Manager",
  HR_APPROVAL_PENDING: "HR Approval Pending",
  APPROVED_BY_HR: "HR Approved",
  REJECTED_BY_HR: "Rejected by HR",
  ELIGIBLE: "Eligible",
  NOT_ELIGIBLE: "Not Eligible",
};

const ALL_STATUSES = Object.keys(STATUS_MAP);

const mapStatus = (status: string) => STATUS_MAP[status] || status || "—";

const formatTimestamp = (ts: string) => {
  if (!ts) return "—";
  // Backend sends timestamps without a timezone suffix but they are UTC;
  // mark them as such so the Date is interpreted correctly, then let
  // toLocaleString render them in the viewer's own local timezone.
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(ts);
  const date = new Date(hasTimezone ? ts : `${ts}Z`);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const PRIORITY_LABELS: Record<string, string> = {
  NOT_SET: "No Priority",
  HIGH: "First Priority",
  TOP: "Second Priority",
};

const formatInfoLabel = (key: string): string =>
  key
    .replace(/_/g, " ")
    .split(" ")
    .map((word) =>
      word.toLowerCase() === "hr"
        ? "HR"
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" ");

const formatInfoValue = (value: unknown, t: (key: string) => string): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (value === true || value === "true") return t("Yes");
  if (value === false || value === "false") return t("No");
  if (typeof value === "string" && PRIORITY_LABELS[value]) {
    return t(PRIORITY_LABELS[value]);
  }
  if (typeof value === "string") {
    return value.replace(/\bhr\b/gi, "HR");
  }
  return String(value);
};

const formatInfoEntries = (
  info: any,
  t: (key: string) => string
): [string, string][] => {
  if (!info || typeof info !== "object") return [];
  return Object.entries(info).map(([k, v]) => [
    formatInfoLabel(k),
    formatInfoValue(v, t),
  ]);
};

interface InfoModalProps {
  title: string;
  entries: [string, string][];
  onClose: () => void;
  t: (key: string) => string;
}

const InfoModal = ({ title, entries, onClose, t }: InfoModalProps) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="bg-white rounded-lg shadow-lg w-[90%] sm:w-[480px] max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#c6c6c6]">
        <h2 className="font-semibold text-base">{title}</h2>
        <button
          className="text-gray-500 hover:text-gray-800 text-xl leading-none"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="overflow-y-auto px-5 py-4 flex flex-col gap-3">
        {entries.length === 0 ? (
          <p className="text-sm text-gray-500">—</p>
        ) : (
          entries.map(([key, value]) => (
            <div key={key} className="flex flex-col gap-0.5">
              <span className="text-xs font-semibold text-gray-600">
                {key}
              </span>
              <span className="text-sm text-gray-800">{value}</span>
            </div>
          ))
        )}
      </div>
      <div className="px-5 py-3 border-t border-[#c6c6c6] flex justify-end">
        <button
          className="px-4 py-2 bg-[#961e38] text-white rounded hover:bg-[#752938] text-sm"
          onClick={onClose}
        >
          {t("Close")}
        </button>
      </div>
    </div>
  </div>
);

function AuditTrailPage({ role, tabs, selectedTabId, tabType }: AuditTrailPageProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { items, loading, downloadLoading, totalItemCount, totalPageCount } =
    useSelector((state: RootState) => state.audit);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Draft filter state — what the user is typing/selecting
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [operatedByRole, setOperatedByRole] = useState("");
  const [operatedByUsername, setOperatedByUsername] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseType, setCourseType] = useState("");
  const [itemType, setItemType] = useState("");
  const [oldStatus, setOldStatus] = useState("");
  const [newStatus, setNewStatus] = useState("");

  // Applied filter state — only updates when "Apply Filters" is clicked
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>({});

  // Info popup state
  const [infoModal, setInfoModal] = useState<{
    title: string;
    entries: [string, string][];
  } | null>(null);

  const buildFilters = (): AuditFilters => {
    const f: AuditFilters = {};
    if (dateFrom) f.date_from = dateFrom;
    if (dateTo) f.date_to = dateTo;
    if (operatedByRole) f.operated_by_role = operatedByRole;
    if (operatedByUsername) f.operated_by_username = operatedByUsername;
    if (courseName) f.course_name = courseName;
    if (courseType) f.course_type = courseType;
    if (itemType) f.item_type = itemType;
    if (oldStatus) f.old_status = oldStatus;
    if (newStatus) f.new_status = newStatus;
    return f;
  };

  const fetchData = (page: number, pageSize: number, filters: AuditFilters) => {
    dispatch(
      getAuditTrail({
        role,
        page,
        page_size: pageSize,
        ...filters,
      })
    );
  };

  // Fetch on mount and whenever appliedFilters changes (triggered by Apply Filters button)
  useEffect(() => {
    setCurrentPage(1);
    fetchData(1, itemsPerPage, appliedFilters);
  }, [appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters(buildFilters());
  };

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setOperatedByRole("");
    setOperatedByUsername("");
    setCourseName("");
    setCourseType("");
    setItemType("");
    setOldStatus("");
    setNewStatus("");
    setAppliedFilters({});
  };

  const handlePaginationChange = ({
    page,
    itemsPerPage: ipp,
  }: {
    page: number;
    itemsPerPage: number;
  }) => {
    setCurrentPage(page);
    setItemsPerPage(ipp);
    fetchData(page, ipp, appliedFilters);
  };

  const handleExport = () => {
    dispatch(
      downloadAuditTrail({
        role,
        page: currentPage,
        page_size: itemsPerPage,
        ...appliedFilters,
      })
    );
  };

  const openInfoModal = (title: string, info: any) => {
    setInfoModal({ title, entries: formatInfoEntries(info, t) });
  };

  const isMobileOrTablet = window.innerWidth < 1024;

  return (
    <div className="flex flex-col items-start gap-8">
      {!isMobileOrTablet && <HomeButton />}
      {isMobileOrTablet ? (
        <>
          <TabNavbar id="" tabs={tabs} selectedTabId={selectedTabId} type={tabType} />
          <HomeButton />
          <Header />
        </>
      ) : (
        <>
          <Header />
          <TabNavbar id="" tabs={tabs} selectedTabId={selectedTabId} type={tabType} />
        </>
      )}

      {/* Filter toggle + Export */}
      <div className="w-full flex flex-wrap justify-between items-center gap-3">
        <button
          className="px-4 py-2 border border-[#961e38] text-[#961e38] rounded hover:bg-[#961e38] hover:text-white transition"
          onClick={() => setIsFilterOpen((o) => !o)}
        >
          {isFilterOpen ? t("Hide Filters") : t("Show Filters")}
        </button>
        <button
          className="px-4 py-2 bg-[#961e38] text-white rounded hover:bg-[#752938] transition disabled:opacity-50"
          onClick={handleExport}
          disabled={downloadLoading}
        >
          {downloadLoading ? t("Exporting...") : t("Export CSV")}
        </button>
      </div>

      {/* Filter panel */}
      {isFilterOpen && (
        <div className="w-full bg-[#f9f9f9] border border-[#c6c6c6] rounded p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Date From")}</label>
              <input
                type="date"
                className="border p-2 rounded text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Date To")}</label>
              <input
                type="date"
                className="border p-2 rounded text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Operator Role")}</label>
              <select
                className="border p-2 rounded text-sm"
                value={operatedByRole}
                onChange={(e) => setOperatedByRole(e.target.value)}
              >
                <option value="">{t("All")}</option>
                <option value="ADMIN">Admin</option>
                <option value="HR">HR</option>
                <option value="MANAGER">Manager</option>
                <option value="LEARNER">Learner</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Username")}</label>
              <input
                type="text"
                className="border p-2 rounded text-sm"
                placeholder={t("Enter username")}
                value={operatedByUsername}
                onChange={(e) => setOperatedByUsername(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Course Name")}</label>
              <input
                type="text"
                className="border p-2 rounded text-sm"
                placeholder={t("Enter course name")}
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Course Type")}</label>
              <select
                className="border p-2 rounded text-sm"
                value={courseType}
                onChange={(e) => setCourseType(e.target.value)}
              >
                <option value="">{t("All")}</option>
                <option value="LOCAL">Local</option>
                <option value="GROUP">Group</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Item Type")}</label>
              <select
                className="border p-2 rounded text-sm"
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
              >
                <option value="">{t("All")}</option>
                <option value="SESSION">Session</option>
                <option value="APPLICATION">Application</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("Old Status")}</label>
              <select
                className="border p-2 rounded text-sm"
                value={oldStatus}
                onChange={(e) => setOldStatus(e.target.value)}
              >
                <option value="">{t("All")}</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_MAP[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">{t("New Status")}</label>
              <select
                className="border p-2 rounded text-sm"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">{t("All")}</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_MAP[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              className="text-sm bg-[#961e38] text-white py-2 px-4 rounded hover:bg-[#752938] transition"
              onClick={handleApplyFilters}
            >
              {t("Apply Filters")}
            </button>
            <button
              className="text-sm border border-[#961e38] text-[#961e38] py-2 px-4 rounded hover:bg-[#f9e8eb] transition"
              onClick={handleClearFilters}
            >
              {t("Clear Filters")}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="w-full h-full">
        {/* Mobile cards */}
        <div className="block md:hidden w-full">
          {loading ? (
            <div className="flex items-center w-full justify-center mt-4 mb-4">
              <SpinnerLoader />
            </div>
          ) : items.length > 0 ? (
            items.map((item: any, i: number) => (
              <div
                key={i}
                className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
              >
                {[
                  [t("Type"), item.item_type],
                  [t("Name"), item.display_name],
                  [t("Course"), item.course_name],
                  [t("Old Status"), mapStatus(item.old_status)],
                  [t("New Status"), mapStatus(item.new_status)],
                  [
                    t("Operated By"),
                    `${item.operated_by_username || "—"}${item.operated_by_role ? ` (${item.operated_by_role})` : ""}`,
                  ],
                  [t("Timestamp"), formatTimestamp(item.transition_timestamp)],
                ].map(([label, value]) => (
                  <div key={label} className="px-4 border-b border-[#c6c6c6] pb-2 pt-1">
                    <h3 className="font-[600] pb-1 text-sm">{label}</h3>
                    <p className="text-sm break-words">{value || "—"}</p>
                  </div>
                ))}
                {/* Item Information */}
                <div className="px-4 border-b border-[#c6c6c6] pb-2 pt-1">
                  <h3 className="font-[600] pb-1 text-sm">{t("Item Information")}</h3>
                  {item.item_information ? (
                    <button
                      className="text-xs px-3 py-1 border border-[#961e38] text-[#961e38] rounded hover:bg-[#961e38] hover:text-white transition"
                      onClick={() =>
                        openInfoModal(t("Item Information"), item.item_information)
                      }
                    >
                      {t("Show")}
                    </button>
                  ) : (
                    <p className="text-sm">—</p>
                  )}
                </div>
                {/* Additional Information */}
                <div className="px-4 pb-2 pt-1">
                  <h3 className="font-[600] pb-1 text-sm">{t("Additional Information")}</h3>
                  {item.additional_information ? (
                    <button
                      className="text-xs px-3 py-1 border border-[#961e38] text-[#961e38] rounded hover:bg-[#961e38] hover:text-white transition"
                      onClick={() =>
                        openInfoModal(
                          t("Additional Information"),
                          item.additional_information
                        )
                      }
                    >
                      {t("Show")}
                    </button>
                  ) : (
                    <p className="text-sm">—</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="w-full text-center py-5">{t("No Data Available")}</p>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block w-full h-full overflow-x-auto">
          <table className="w-full border-collapse min-w-[1100px]">
            <thead className="bg-[#961e38] text-white">
              <tr>
                <th className="py-2 px-3 text-left text-sm">{t("Type")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Name")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Course")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Old Status")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("New Status")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Operated By")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Timestamp")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Item Information")}</th>
                <th className="py-2 px-3 text-left text-sm">{t("Additional Information")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <SpinnerLoader />
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item: any, i: number) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#f9f9f9]"}>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {item.item_type || "—"}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {item.display_name || "—"}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {item.course_name || "—"}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {mapStatus(item.old_status)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {mapStatus(item.new_status)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {item.operated_by_username || "—"}
                      {item.operated_by_role && (
                        <span className="block text-xs text-gray-500">
                          {item.operated_by_role}
                        </span>
                      )}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm whitespace-nowrap">
                      {formatTimestamp(item.transition_timestamp)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {item.item_information ? (
                        <button
                          className="text-xs px-3 py-1 border border-[#961e38] text-[#961e38] rounded hover:bg-[#961e38] hover:text-white transition"
                          onClick={() =>
                            openInfoModal(t("Item Information"), item.item_information)
                          }
                        >
                          {t("Show")}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-3 text-sm">
                      {item.additional_information ? (
                        <button
                          className="text-xs px-3 py-1 border border-[#961e38] text-[#961e38] rounded hover:bg-[#961e38] hover:text-white transition"
                          onClick={() =>
                            openInfoModal(
                              t("Additional Information"),
                              item.additional_information
                            )
                          }
                        >
                          {t("Show")}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    {t("No Data Available")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalItemCount > 0 && (
          <Pagination
            totalPages={totalPageCount}
            total_item_count={totalItemCount}
            _currentPage={currentPage}
            _itemsPerPage={itemsPerPage}
            isCoursePage={true}
            handleChange={handlePaginationChange}
          />
        )}
      </div>

      {/* Info popup modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          entries={infoModal.entries}
          onClose={() => setInfoModal(null)}
          t={t}
        />
      )}
    </div>
  );
}

export default AuditTrailPage;
